import {
  inject,
  Injectable,
  Renderer2,
  RendererFactory2
} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ColorService {
  // ColorService is providedIn 'root', where Renderer2 is not directly
  // injectable. Build one via RendererFactory2 so root injection is always
  // safe (mirrors ThemeService). See JMKW-1864 PR review.
  private readonly _rendererFactory = inject(RendererFactory2);
  private readonly _renderer: Renderer2 = this._rendererFactory.createRenderer(
    null,
    null
  );

  getColor(variableName: string): string | null {
    const root = this._renderer.parentNode(
      this._renderer.selectRootElement('html')
    );
    return getComputedStyle(root).getPropertyValue(variableName).trim() || null;
  }

  calculateInterpolatedColor(
    colors: string[],
    normalizedDifference: number
  ): string {
    const colorIndex = Math.floor(normalizedDifference * (colors.length - 1));
    const nextColorIndex = Math.min(colorIndex + 1, colors.length - 1);
    const colorFraction = (normalizedDifference * (colors.length - 1)) % 1;

    const startColor = this._hexToRgb(colors[colorIndex]);
    const endColor = this._hexToRgb(colors[nextColorIndex]);
    const interpolatedColor = this._interpolateColor(
      startColor,
      endColor,
      colorFraction
    );

    return `rgba(${interpolatedColor.r}, ${interpolatedColor.g}, ${interpolatedColor.b}, 1)`;
  }

  private _interpolateColor(
    color1: { r: number; g: number; b: number },
    color2: { r: number; g: number; b: number },
    fraction: number
  ) {
    return {
      r: Math.round(color1.r + (color2.r - color1.r) * fraction),
      g: Math.round(color1.g + (color2.g - color1.g) * fraction),
      b: Math.round(color1.b + (color2.b - color1.b) * fraction)
    };
  }

  private _hexToRgb(hex: string) {
    const bigint = parseInt(hex.slice(1), 16);
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
  }
}
